<template>
  <V2Browse v-if="browseMenu" :store-id="storeID" :menu="browseMenu" />
  <template v-else>
    <div class="z-20 w-full fixed top-0">
      <MerchantNav :disable-hide-on-scroll="true" />
    </div>

    <div v-fade-in v-if="loading" class="w-full">
      <Loading class="mt-10" />
    </div>
    <div v-show="!loading">
      <div v-if="!hasAccess" class="w-full max-w-5xl mx-auto h-full py-20">
        <div class="px-2 md:px-0 md:w-1/2 mx-auto pt-4 sm:pt-10">
          <div class="flex justify-start items-center space-x-2">
            <LockClosedIcon class="h-5 w-5" />
            <div class="text-2xl font-bold text-gray-brand-900 tracking-tighter leading-6">
              The {{ store?.attributes.store_name }} store is locked.
            </div>
          </div>
          <div v-if="store?.attributes.allow_password_access" className="mt-6 max-w-md">
            <div className="flex gap-x-4">
              <label htmlFor="password" className="sr-only">Store password</label>
              <input
                id="password"
                name="password"
                type="password"
                v-model="password"
                required
                className="min-w-0 flex-auto rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-brand-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-brand-600 sm:text-sm sm:leading-6"
                placeholder="Store password"
                @keydown="
                  (e) => {
                    if (e.key === 'Enter') enterPassword()
                  }
                "
              />
              <PrimaryButton
                @click="enterPassword"
                :size="'lg'"
                :justify="'justify-center'"
                :active="password?.length > 0"
                :streaking="password?.length > 0"
                :is-loading="loading"
              >
                Enter store
                <template #right-icon><ArrowRightIcon class="ml-2 h-4" /></template>
              </PrimaryButton>
            </div>
          </div>
          <h3 class="mt-8 text-md text-gray-brand-700 w-full leading-5 space-y-2">
            <SecondaryButton v-if="!isLoggedIn" @click="goToLogin" class="w-full" :size="'lg'">Login</SecondaryButton>
            <SecondaryButton @click="canText ? goTo(phoneLink) : goTo(emailLink)" class="w-full" :size="'lg'">
              <span v-if="canText">Text us to get access.</span>
              <span v-else>Email us to get access.</span>
            </SecondaryButton>
          </h3>
        </div>
      </div>
      <div v-else-if="isV2 && v2PriorBottle" class="w-full max-w-5xl mx-auto h-full py-20">
        <div class="px-2 md:px-0 md:w-1/2 mx-auto pt-4 sm:pt-10">
          <div class="text-2xl font-bold text-gray-brand-900 tracking-tighter leading-7">Pick up where you</div>
          <div class="text-2xl font-garamond italic font-bold text-brand-600 tracking-tight">left off...</div>

          <V2PriorBottleCard :cart="v2PriorBottle" @proceed="goToBottle" />

          <p class="text-center text-sm text-gray-brand-600 mt-4">
            Looking for something else?
            <a href="#" class="underline font-medium text-gray-brand-900" @click.prevent="newBottle">
              Start a new order
            </a>
          </p>
        </div>
      </div>
      <div v-else-if="hasExistingBottles" class="w-full max-w-5xl mx-auto h-full py-20">
        <div class="px-2 md:px-0 md:w-1/2 mx-auto pt-4 sm:pt-10">
          <div class="text-2xl font-bold text-gray-brand-900 tracking-tighter leading-7">Pick up where you</div>
          <div class="text-2xl font-garamond italic font-bold text-brand-600 tracking-tight">left off...</div>

          <div v-for="bottle in computedBottles" :key="bottle.id">
            <BottleCycleStoreBottleCard :bottle="bottle" :is-new-checkout-flow="true" />
          </div>

          <p class="text-center text-sm text-gray-brand-600 mt-4">
            Looking for something else?
            <a href="#" class="underline font-medium text-gray-brand-900" @click.prevent="newBottle">
              Start a new order
            </a>
          </p>
        </div>
      </div>
    </div>
  </template>
</template>
<script>
import { MerchantNav, Loading } from '@/components'
import BottleCycleStoreBottleCard from '@/components/Checkout/BottleCycleStoreBottleCard'
import V2PriorBottleCard from '@/components/v2/V2PriorBottleCard'
import V2Browse from '@/components/v2/V2Browse'
import { canBrowseMenu, fetchBrowseMenu } from '@/composables/useBrowseMenu'
import BasicButton from '@/components/Molecules/BasicButton'
import PrimaryButton from '@/components/Molecules/PrimaryButton'
import SecondaryButton from '@/components/Molecules/SecondaryButton'
import mapRelationships from '../utils/mapRelationships'
import { LockClosedIcon, ArrowRightIcon } from '@heroicons/vue/outline'
import { usePriorBottleQuery } from '@/composables/usePriorBottleQuery'
import { useRoute } from 'vue-router'

export default {
  components: {
    MerchantNav,
    BottleCycleStoreBottleCard,
    V2PriorBottleCard,
    V2Browse,
    BasicButton,
    Loading,
    PrimaryButton,
    SecondaryButton,
    LockClosedIcon,
    ArrowRightIcon
  },

  setup() {
    const route = useRoute()
    const priorBottleQuery = usePriorBottleQuery(() => route.params.storeId)

    return {
      v2PriorBottle: priorBottleQuery.priorBottle,
      v2PriorBottleLoading: priorBottleQuery.isLoading
    }
  },
  data() {
    return {
      bottles: [],
      newBottleIsLoading: false,
      existingBottleIsLoading: false,
      loading: true,
      enteredPassword: false,
      password: '',
      browseMenu: null
    }
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters.isLoggedIn
    },
    storeID() {
      return this.$route.params.storeId
    },
    store() {
      return this.$store.state.objects.store?.[this.storeID]
    },
    locked() {
      return this.store?.attributes.locked
    },
    hasAccess() {
      return !this.locked || this.store?.attributes.customer_has_access || this.enteredPassword
    },
    hasExistingBottles() {
      return this.bottles.length > 0
    },
    showsPriorOrders() {
      return this.hasAccess && ((this.isV2 && !!this.v2PriorBottle) || this.hasExistingBottles)
    },
    computedBottles() {
      return mapRelationships(this.$store.state, this.bottles)
    },
    info() {
      return this.$store.getters.info
    },
    phone() {
      return this.info?.attributes?.phone
    },
    phoneLink() {
      return 'sms:' + (this.phone ? this.phone : '')
    },
    canText() {
      return this.phone
    },
    emailLink() {
      return 'mailto:' + (this.info?.attributes?.email ? this.info?.attributes?.email : '')
    },
    isV2() {
      return this.$store.getters.hasV2Checkout
    }
  },
  async mounted() {
    window.scrollTo(0, 0)
    await Promise.all([
      this.$store.dispatch('showStore', {
        storeId: this.storeID,
        storeSlug: this.$route.params.storeSlug
      }),
      this.$store.dispatch('getInfo').catch(() => null)
    ])
    if (this.hasAccess && !this.isV2) {
      await this.loadBottles()
    }
    if (this.isV2 && this.hasAccess) {
      await this.waitForV2PriorBottle()
      if (!this.v2PriorBottle) {
        if (await this.openBrowseMenu()) return
        await this.loadBottles()
        return
      }
      if (!(this.v2PriorBottle.cart_items?.length > 0)) {
        if (this.v2HasValidFulfillmentSlot(this.v2PriorBottle)) {
          console.log('[BottleFactory] v2 auto-edit: 0 items + valid slot (or no slot) -> Selection', {
            bottleId: this.v2PriorBottle.bottle_id
          })
          this.$router.replace({ name: 'Selection', params: { bottleid: this.v2PriorBottle.bottle_id } })
        } else {
          console.log('[BottleFactory] v2 auto-new: 0 items + stale slot -> newBottle', {
            bottleId: this.v2PriorBottle.bottle_id
          })
          await this.newBottle()
        }
        return
      }
    }
    this.finishLoading()
  },
  methods: {
    // The footer's "View our other menus" carousel waits for SET_MAIN_CONTENT_LOADED. "Pick up where
    // you left off" never sets it: the choice there is the customer's order or a new one (BOT-1739).
    finishLoading() {
      this.loading = false
      if (!this.showsPriorOrders) this.$store.commit('SET_MAIN_CONTENT_LOADED', true)
    },
    async openBrowseMenu() {
      if (!this.$store.getters.menuBeforeCart || this.$store.getters.isLoggedIn) return false
      const menu = await fetchBrowseMenu(this.storeID).catch(() => null)
      if (!canBrowseMenu(menu)) return false
      this.browseMenu = menu
      this.loading = false
      this.$store.commit('SET_MAIN_CONTENT_LOADED', true)
      return true
    },
    waitForV2PriorBottle() {
      return new Promise((resolve) => {
        if (!this.v2PriorBottleLoading) return resolve()
        const unwatch = this.$watch('v2PriorBottleLoading', (loading) => {
          if (!loading) {
            unwatch()
            resolve()
          }
        })
      })
    },
    async loadBottles() {
      const data = await this.$store.dispatch('getStoreBottles', this.storeID)
      if (!Array.isArray(data)) {
        this.$router.replace({ name: 'Selection', params: { bottleid: data?.id } })
        return
      }
      this.bottles = data
      if (this.computedBottles.length !== 1) return
      const only = this.computedBottles[0]
      if (this.cartItemCount(only) > 0) return
      if (this.hasValidFulfillmentSlot(only)) {
        console.log('[BottleFactory] v1 auto-edit: 0 items + valid slot (or no slot) -> Selection', {
          bottleId: only.id
        })
        this.$router.replace({ name: 'Selection', params: { bottleid: only.id } })
      } else {
        console.log('[BottleFactory] v1 auto-new: 0 items + stale slot -> newBottle', { bottleId: only.id })
        await this.newBottle()
      }
    },
    hasValidFulfillmentSlot(bottle) {
      if (!bottle?.relationships?.fulfillment_slot?.data) return true
      const validations = bottle.attributes?.validations
      if (!validations) return false
      return validations.has_fulfillment_errors === false
    },
    v2HasValidFulfillmentSlot(cart) {
      if (!cart?.arrival_date) return true
      return !(cart.slot_errors?.length > 0)
    },
    async newBottle() {
      this.newBottleIsLoading = true
      try {
        const data = await this.$store.dispatch('getNewBottleId', this.storeID)
        if (!data?.id) throw new Error('getNewBottleId returned no bottle id')
        this.$router.push({ name: 'Selection', params: { bottleid: data.id } })
      } finally {
        this.newBottleIsLoading = false
      }
    },
    goToBottle(bottleID) {
      this.$router.push({ name: 'Selection', params: { bottleid: bottleID } })
    },
    cartFor(bottle) {
      return this.$store.getters.cart(bottle.relationships?.cart.data)
    },
    cartItemCount(bottle) {
      const cart = this.$store.getters.cart(bottle.relationships?.cart?.data)
      return mapRelationships(this.$store.state, cart?.relationships?.cart_items?.data).length
    },
    lastEditedTimestamp(bottle) {
      if (!bottle.attributes.updated_at) return ''
      let time = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hourCycle: 'h12'
      }).format(new Date(bottle.attributes.updated_at))
      let day = new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }).format(new Date(bottle.attributes.updated_at))
      return day + ' at ' + time
    },
    async enterPassword() {
      try {
        await this.$store.dispatch('unlockStore', { storeId: this.storeID, password: this.password })
      } catch (error) {
        alert(error.status === 429 ? 'Too many attempts. Please try again in a few minutes.' : 'Incorrect password.')
        return
      }
      this.enteredPassword = true
      this.loading = true
      await this.loadBottles()
      this.finishLoading()
    },
    goToLogin() {
      this.$router.push({ name: 'Login' })
    },
    goTo(link) {
      window.location.href = link
    }
  }
}
</script>
